import React, { useContext, useEffect, useMemo, useRef, useState } from 'react'
import _ from 'lodash'
import { Label } from 'semantic-ui-react'
import MatchItemScoring from './MatchItemScoring'
import { uniqueKey } from 'global/common/keys'
import FullPageWrapper from 'global/wrappers/FullPageWrapper'
import { SportsRtMatchContext } from 'projectSpecific/sports/cnr/contexts/SportsRtMatchContext'
import { useRealtimeUpdates } from 'projectSpecific/sports/useHelpers/useRealtimeResults'
import { SportsMatchContext } from 'projectSpecific/sports/cnr/contexts/SportsMatchContext'
import { useMatchResults } from 'projectSpecific/sports/useHelpers/useMatchResults'
import { useAppUserAccess } from 'global/cnr/contexts/useHelpers/useAppUserAccess'

const _labelSize = 'mini'

const WAIT_INTERVAL = 1000

const getRequiredPoints = (setNumber) => {
  return setNumber === 5 ? 15 : 25
}

const isSetWinningScore = ({
  away,
  home,
  setNumber
}) => {

  const requiredPoints = getRequiredPoints(setNumber)

  const maxScore = Math.max(away, home)
  const scoreDiff = Math.abs(away - home)

  return (
    maxScore >= requiredPoints &&
    scoreDiff >= 2
  )

}

const getCurrentSet = (scores = {}) => {

  const setNumbers = _.chain(scores)
    .keys()
    .map(Number)
    .sortBy()
    .value()


  if (_.isEmpty(setNumbers)) return 1

  console.log('scores', scores)

  const incompleteSet = _.find(setNumbers, setNumber => {
    return scores?.[setNumber]?.complete !== true
    // return scores?.[`set${setNumber}`]?.complete !== true
  })

  console.log('setNumbers', incompleteSet, _.max(setNumbers))

  return incompleteSet || (_.max(setNumbers))

}

const getMatchScore = (scores = {}) => {

  let away = 0
  let home = 0

  _.forEach(scores, set => {

    if (!set?.complete) return

    if ((set.away || 0) > (set.home || 0)) {
      away += 1
    }

    if ((set.home || 0) > (set.away || 0)) {
      home += 1
    }

  })

  return {
    away,
    home
  }

}

const ActionLabel = ({ children, color, onClick, restrictAccess }) => {
  return (
    <Label
      color={restrictAccess ? 'grey' : color}
      onClick={!restrictAccess && onClick}
      style={{
        cursor: 'pointer',
        userSelect: 'none',
        textAlign: 'center'
      }}
    >
      {children}
    </Label>
  )

}

const RealtimeScorekeeper = () => {

  const appUserAccess = useAppUserAccess()
  const appUserInfo = appUserAccess ? { displayName: appUserAccess.displayName, uid: appUserAccess.uid } : {}
  const { displayName, uid } = appUserInfo ?? {}

  const sportsMatchContext = useContext(SportsMatchContext)
  const { sportsMatch_state } = sportsMatchContext ?? {}
  const { match_true } = sportsMatch_state ?? {}

  const { away: awayTeam, home: homeTeam } = match_true ?? {}

  const _umr = useMatchResults({ match_true })
  const { rtrs } = _umr ?? {}
  const { updateRtMatch, results_realtime } = useRealtimeUpdates()

  const rtr = rtrs?.[uid]

  const [currentResults, setCurrentResults] = useState()
  const [pendingSetConfirm, setPendingSetConfirm] = useState(null)
  const [restrictAccess, setRestrictAccess] = useState(false)
  const [showSetScores, setShowSetScores] = useState()

  const restrictAccessRef = useRef(false)

  useEffect(() => {
    if (!rtr) return;
    const { score, scores } = rtr
    const _scores = {}
    _.forEach(scores, (score, setNumber) => {
      const _setNumber = setNumber.replace('set', '')
      _scores[_setNumber] = score
    })
    setCurrentResults({
      score,
      scores: _scores
    })
  }, [rtr])

  useEffect(() => {
    if (restrictAccess) {
      const timer = setTimeout(() => {
        restrictAccessRef.current = false
        setRestrictAccess(false)

      }, WAIT_INTERVAL)
      return () => clearTimeout(timer)
    }

  }, [restrictAccess])

  const currentSet = useMemo(() => {
    return getCurrentSet(currentResults?.scores)
  }, [currentResults])

  const _currentSet = currentSet

  const currentSetData = currentResults?.scores?.[_currentSet] || {
    away: 0,
    home: 0
  }

  const matchScore = useMemo(() => {
    return getMatchScore(currentResults?.scores)
  }, [currentResults])

  const handleShowSetScores = () => setShowSetScores(!showSetScores)

  const handleSaveResults = (updatedResults) => {
    setCurrentResults(updatedResults)
    updateRtMatch({ match_rt: { results: updatedResults, setNumber: currentSet }, match_true })
  }

  const handleScoreChange = ({
    e,
    teamType,
    delta,
  }) => {

    e.stopPropagation()

    if (pendingSetConfirm) return;

    // immediate synchronous lock
    if (restrictAccessRef.current) return
    restrictAccessRef.current = true
    setRestrictAccess(true);

    const _currentSet = currentSet

    const currentScore = currentSetData?.[`${teamType}`] || 0

    const newScore = Math.max(0, currentScore + delta)

    const updatedScores = _.cloneDeep(
      currentResults?.scores || {}
    )

    if (!updatedScores[_currentSet]) {
      updatedScores[_currentSet] = {
        away: 0,
        home: 0
      }
    }

    updatedScores[_currentSet] = {
      ...updatedScores[_currentSet],
      [`${teamType}`]: newScore
    }

    const away = updatedScores?.[_currentSet]?.away || 0
    const home = updatedScores?.[_currentSet]?.home || 0
    const updatedMatchScore = getMatchScore(updatedScores)

    const updatedResults = {
      score: updatedMatchScore,
      scores: updatedScores
    }

    handleSaveResults(updatedResults)

    const setWon = isSetWinningScore({
      away,
      home,
      setNumber: currentSet
    })

    if (setWon) {
      setPendingSetConfirm({
        setNumber: currentSet,
        away,
        home
      })
    }
  }

  const handleConfirmSet = (e) => {

    e.stopPropagation()

    if (!pendingSetConfirm) return

    const {
      setNumber,
      away,
      home
    } = pendingSetConfirm

    const updatedScores = _.cloneDeep(
      currentResults?.scores || {}
    )

    updatedScores[setNumber] = {
      ...updatedScores[setNumber],
      complete: true,
      winner:
        away > home
          ? 'away'
          : 'home'
    }

    const updatedMatchScore = getMatchScore(updatedScores)

    const matchComplete =
      updatedMatchScore.away >= 3 ||
      updatedMatchScore.home >= 3

    if (!matchComplete) {

      const nextSet = setNumber + 1

      if (!updatedScores[nextSet]) {
        updatedScores[nextSet] = {
          away: 0,
          home: 0
        }
      }
    }

    const updatedResults = {
      score: updatedMatchScore,
      scores: updatedScores
    }

    handleSaveResults(updatedResults)

    setPendingSetConfirm(null)

  }

  const handleCancelConfirm = (e) => {
    e.stopPropagation()
    setPendingSetConfirm(null)
  }

  const lbl_action = (teamType, add) => {
    const delta = add ? 1 : -1
    const color = add ? 'green' : 'red'
    const text = add ? '+' : '-'
    return <Label
      color={restrictAccess ? 'grey' : color}
      onClick={(e) => handleScoreChange({ e, teamType, delta })}
    >
      {text}
    </Label>
  }

  if (showSetScores) {
    return (
      <FullPageWrapper
        content={<MatchItemScoring key={uniqueKey('crm', 'mss', match_true._itemKey)} setScoring={true} />}
        handleCancel={handleShowSetScores}
        css={{ container: 'app-dark-mode' }}
      ></FullPageWrapper>
    )
  }

  const upDownLabels = (teamType, setScore) => <div>
    {lbl_action(teamType, false)}
    <Label>{setScore || 0}</Label>
    {lbl_action(teamType, true)}
  </div>

  return (
    <div className={`match-rts ${restrictAccess ? 'rac' : ''}`}>
      {/* Header */}
      <div className='match-rts-scorer'>
        <div>{displayName}</div>
      </div>
      <div className='match-rts-header'>
        <div><Label size={_labelSize}>{awayTeam?.name || 'Away'}</Label></div>
        <div>
          <Label color='black' size={'small'} onClick={(e) => handleShowSetScores()}>
            {`${matchScore.away} - ${matchScore.home}`}
          </Label>
        </div>
        <div><Label size={_labelSize}>{homeTeam?.name || 'Home'}</Label></div>
      </div>

      {/* Controls */}
      <div className='match-rts-controls'>
        {upDownLabels('away', currentSetData.away)}
        <Label color='black'>Set {currentSet}</Label>
        {upDownLabels('home', currentSetData.home)}
      </div>

      {/* Confirm Set */}
      {
        pendingSetConfirm && (
          <div
            style={{
              marginTop: 8,
              display: 'flex',
              justifyContent: 'center',
              alignItems: 'center',
              gap: 6,
              flexWrap: 'wrap'
            }}
          >

            <Label color='orange'>
              Confirm Set {pendingSetConfirm.setNumber}?
            </Label>

            <Label>{`${pendingSetConfirm.away} - ${pendingSetConfirm.home}`}</Label>

            <ActionLabel
              color='green'
              onClick={handleConfirmSet}
            >
              ✓
            </ActionLabel>

            <ActionLabel
              color='red'
              onClick={handleCancelConfirm}
            >
              ✕
            </ActionLabel>

          </div>
        )
      }

    </div>
  )

}

export default RealtimeScorekeeper